import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { TFunction } from 'i18next';
import t, { AssetLibraryDispatch, Filter, FilterValue } from './assetLibraryFilter.types';
import {
  getFilters,
  updateFilterTypeLabel as updateFilterTypeLabelApi,
  toggleFilterTypeActive as toggleFilterTypeActiveApi,
  addFilterValue as addFilterValueApi,
  editFilterValue as editFilterValueApi,
  deleteFilterValue as deleteFilterValueApi,
  createFilterType as createFilterTypeApi,
  deleteFilterType as deleteFilterTypeApi,
} from '../../../../services/assetLibraryFilter.service';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import { ERROR_MESSAGE } from '../../../../Components/helper';

interface ApiResponse<TData = unknown> {
  code?: number;
  message?: string;
  data?: TData;
}

interface ApiErrorData {
  errMsg?: string;
}

interface RejectedApiCall {
  response?: {
    data?: ApiResponse<ApiErrorData>;
  };
}

export type AssetLibraryThunkAction<TReturn = void> = ThunkAction<
  Promise<TReturn>,
  unknown,
  unknown,
  AnyAction
>;

/**
 * Extract a user-facing message from a failed API envelope
 * @param res - Response envelope; on failure `data` carries an `errMsg`, on success it carries the entity
 * @returns The server-supplied reason, falling back to the generic error message
 */
const getApiErrorMessage = (res: ApiResponse<unknown> | undefined): string => {
  const errorData = res?.data as ApiErrorData | undefined;
  return errorData?.errMsg || res?.message || ERROR_MESSAGE;
};

/**
 * Extract a user-facing message from a rejected API call
 * @param error - Value the API client rejected with; axios carries the server envelope on `response.data`
 * @returns The server-supplied reason (e.g. a 409 conflict), falling back to the generic error message
 */
const getRejectedApiErrorMessage = (error: unknown): string =>
  getApiErrorMessage((error as RejectedApiCall)?.response?.data);

export const fetchFilters = (): AssetLibraryThunkAction => async (
  dispatch: AssetLibraryDispatch
): Promise<void> => {
  try {
    dispatch({ type: t.SET_LOADING, payload: true });
    const res: ApiResponse<Filter[]> = await getFilters();
    if (res?.code === 200) {
      dispatch({ type: t.FETCH_FILTERS_SUCCESS, payload: res.data });
    } else {
      dispatch({ type: t.SET_ERROR, payload: res?.message || 'Failed to fetch filters' });
    }
  } catch {
    dispatch({ type: t.SET_LOADING, payload: false });
    toasterMsg(ERROR_MESSAGE, 'danger');
  }
};

export const updateFilterTypeLabel = (
  filterTypeId: string,
  label: string,
  translationFn: TFunction
): AssetLibraryThunkAction => async (dispatch: AssetLibraryDispatch): Promise<void> => {
  try {
    const res = await updateFilterTypeLabelApi(filterTypeId, label);
    if (res?.code === 200) {
      dispatch({
        type: t.UPDATE_FILTER_LABEL,
        payload: { _id: filterTypeId, label },
      });
      toasterMsg(translationFn('ASSET_LIBRARY.FILTER_LABEL_UPDATED'), 'success');
    } else {
      toasterMsg(getApiErrorMessage(res), 'danger');
    }
  } catch {
    toasterMsg(ERROR_MESSAGE, 'danger');
  }
};

export const toggleFilterTypeActive = (
  filterTypeId: string,
  isActive: boolean,
  translationFn: TFunction
): AssetLibraryThunkAction => async (dispatch: AssetLibraryDispatch): Promise<void> => {
  try {
    const res = await toggleFilterTypeActiveApi(
      filterTypeId,
      isActive
    );
    if (res?.code === 200) {
      dispatch({
        type: t.TOGGLE_FILTER_ACTIVE,
        payload: { _id: filterTypeId, isActive },
      });
      const msgKey = isActive
        ? 'ASSET_LIBRARY.FILTER_ACTIVATED'
        : 'ASSET_LIBRARY.FILTER_DEACTIVATED';
      toasterMsg(translationFn(msgKey), 'success');
    } else {
      toasterMsg(getApiErrorMessage(res), 'danger');
    }
  } catch {
    toasterMsg(ERROR_MESSAGE, 'danger');
  }
};

/**
 * Creates a value under a filter type and returns the persisted entity
 * @param filterTypeId - MongoDB ObjectId string of the owning filter type
 * @param label - Display label for the new value
 * @param translationFn - i18next translation function for toaster copy
 * @returns The created value (carrying its server-assigned `_id`) or null when the call fails
 */
export const addFilterValue = (
  filterTypeId: string,
  label: string,
  translationFn: TFunction
): AssetLibraryThunkAction<FilterValue | null> => async (
  dispatch: AssetLibraryDispatch
): Promise<FilterValue | null> => {
  try {
    const res: ApiResponse<FilterValue & ApiErrorData> = await addFilterValueApi(
      filterTypeId,
      label
    );
    if (res?.code === 200 || res?.code === 201) {
      const createdValue = res.data as FilterValue;
      dispatch({
        type: t.ADD_VALUE,
        payload: { filterTypeId, value: createdValue },
      });
      toasterMsg(translationFn('ASSET_LIBRARY.VALUE_ADDED'), 'success');
      return createdValue;
    }
    toasterMsg(getApiErrorMessage(res), 'danger');
    return null;
  } catch {
    toasterMsg(ERROR_MESSAGE, 'danger');
    return null;
  }
};

export const editFilterValue = (
  filterTypeId: string,
  valueId: string,
  label: string,
  translationFn: TFunction
): AssetLibraryThunkAction<boolean> => async (
  dispatch: AssetLibraryDispatch
): Promise<boolean> => {
  try {
    const res: ApiResponse<FilterValue & ApiErrorData> = await editFilterValueApi(
      filterTypeId,
      valueId,
      label
    );
    if (res?.code === 200) {
      dispatch({
        type: t.EDIT_VALUE,
        payload: { filterTypeId, value: res.data as FilterValue },
      });
      toasterMsg(translationFn('ASSET_LIBRARY.VALUE_UPDATED'), 'success');
      return true;
    }
    toasterMsg(getApiErrorMessage(res), 'danger');
    return false;
  } catch {
    toasterMsg(ERROR_MESSAGE, 'danger');
    return false;
  }
};

/**
 * Deletes a value from a filter type, surfacing the server reason when it refuses
 * @param filterTypeId - MongoDB ObjectId string of the parent filter type
 * @param valueId - MongoDB ObjectId string of the value to delete
 * @param translationFn - i18next translation function for toaster copy
 * @returns True when the value was deleted, false when the server rejected it (e.g. the value is in use)
 */
export const deleteFilterValue = (
  filterTypeId: string,
  valueId: string,
  translationFn: TFunction
): AssetLibraryThunkAction<boolean> => async (
  dispatch: AssetLibraryDispatch
): Promise<boolean> => {
  try {
    const res = await deleteFilterValueApi(filterTypeId, valueId);
    if (res?.code === 200) {
      dispatch({
        type: t.DELETE_VALUE,
        payload: { filterTypeId, valueId },
      });
      toasterMsg(translationFn('ASSET_LIBRARY.VALUE_DELETED'), 'success');
      return true;
    }
    toasterMsg(getApiErrorMessage(res), 'danger');
    return false;
  } catch (error) {
    toasterMsg(getRejectedApiErrorMessage(error), 'danger');
    return false;
  }
};

export const setActivePanel = (filterTypeId: string | null): AnyAction => ({
  type: t.SET_ACTIVE_PANEL,
  payload: filterTypeId,
});

/**
 * Translates the business-error key a rejected 4xx call carries; anything else gets the generic message
 * @param error - Value the API client rejected with
 * @param translationFn - i18next translation function
 * @returns A user-facing, translated message
 */
const getTranslatedRejectionMessage = (error: unknown, translationFn: TFunction): string => {
  const response = (error as RejectedApiCall & { response?: { status?: number } })?.response;
  const status = response?.status ?? 0;
  const messageKey = response?.data?.message;
  if (status >= 400 && status < 500 && messageKey) {
    return translationFn(messageKey, { defaultValue: ERROR_MESSAGE });
  }
  return ERROR_MESSAGE;
};

/**
 * Creates a custom filter type and appends it to the list
 * @param label - Display label of the new filter type
 * @param translationFn - i18next translation function for toaster copy
 * @returns The created filter type, or null when the server refused it (e.g. the 5-type limit)
 */
export const createFilterType = (
  label: string,
  translationFn: TFunction
): AssetLibraryThunkAction<Filter | null> => async (
  dispatch: AssetLibraryDispatch
): Promise<Filter | null> => {
  try {
    const res: ApiResponse<Filter> = await createFilterTypeApi(label);
    const createdFilter = res?.data;
    if (res?.code === 201 && createdFilter) {
      dispatch({ type: t.ADD_FILTER_TYPE, payload: createdFilter });
      toasterMsg(translationFn('ASSET_LIBRARY.FILTER_TYPE_CREATED'), 'success');
      return createdFilter;
    }
    toasterMsg(ERROR_MESSAGE, 'danger');
    return null;
  } catch (error) {
    toasterMsg(getTranslatedRejectionMessage(error, translationFn), 'danger');
    return null;
  }
};

/**
 * Deletes a custom filter type with its values and removes it from the list
 * @param filterTypeId - MongoDB ObjectId string of the filter type
 * @param translationFn - i18next translation function for toaster copy
 * @returns True when deleted, false when the server refused it (e.g. a value is in use)
 */
export const deleteFilterType = (
  filterTypeId: string,
  translationFn: TFunction
): AssetLibraryThunkAction<boolean> => async (
  dispatch: AssetLibraryDispatch
): Promise<boolean> => {
  try {
    const res = await deleteFilterTypeApi(filterTypeId);
    if (res?.code === 200) {
      dispatch({ type: t.DELETE_FILTER_TYPE, payload: { _id: filterTypeId } });
      toasterMsg(translationFn('ASSET_LIBRARY.FILTER_TYPE_DELETED'), 'success');
      return true;
    }
    toasterMsg(ERROR_MESSAGE, 'danger');
    return false;
  } catch (error) {
    toasterMsg(getTranslatedRejectionMessage(error, translationFn), 'danger');
    return false;
  }
};
